const RECENTLY_VIEWED_STORAGE_VERSION = 2;

function normalizeRecentlyViewedEntry(entry) {
  if (typeof entry === 'string' && entry) return { handle: entry };
  if (!entry || typeof entry !== 'object' || typeof entry.handle !== 'string' || !entry.handle) {
    return null;
  }

  return {
    id: entry.id || null,
    handle: entry.handle,
    title: entry.title || '',
    url: entry.url || '',
    featured_image: entry.featured_image || '',
    badge: entry.badge || '',
    is_bestseller: Boolean(entry.is_bestseller),
  };
}

function readRecentlyViewedStore(storageKey) {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (!raw) return [];

    // Legacy unversioned array -> migrate to current shape without prices.
    if (Array.isArray(raw)) {
      const items = raw.map(normalizeRecentlyViewedEntry).filter(Boolean);
      writeRecentlyViewedStore(storageKey, items);
      return items;
    }

    if (!raw || typeof raw !== 'object') return [];

    if (raw.v !== RECENTLY_VIEWED_STORAGE_VERSION) {
      localStorage.removeItem(storageKey);
      return [];
    }

    return (Array.isArray(raw.items) ? raw.items : []).map(normalizeRecentlyViewedEntry).filter(Boolean);
  } catch (error) {
    return [];
  }
}

function writeRecentlyViewedStore(storageKey, items) {
  localStorage.setItem(
    storageKey,
    JSON.stringify({
      v: RECENTLY_VIEWED_STORAGE_VERSION,
      items: items.slice(0, 12),
    })
  );
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.section = this.closest('[data-recently-viewed-section]');
        if (!this.section) return;

        this.track = this.querySelector('[data-recently-viewed-track]');
        this.previousButton = this.section.querySelector('[data-recently-viewed-previous]');
        this.nextButton = this.section.querySelector('[data-recently-viewed-next]');
        this.compact = this.section.dataset.compact === 'true';
        this.mergeBestsellers = this.section.dataset.mergeBestsellers === 'true';
        this.isGrouped = this.section.dataset.layout === 'grouped';
        this.limit = Number(this.section.dataset.limit) || 12;
        this.viewedHeading = this.section.dataset.viewedHeading || 'Recently viewed';
        this.bestsellersHeading = this.section.dataset.bestsellersHeading || 'Shop Bestsellers';

        const viewedEntries = this.getStoredEntries();
        if (!viewedEntries.length) return;

        const entriesToResolve = viewedEntries.slice(0, this.limit);

        Promise.all(entriesToResolve.map((entry) => this.resolveProduct(entry)))
          .then((viewedProducts) => {
            const validViewed = viewedProducts.filter(Boolean);
            if (!validViewed.length) return;

            const products = this.mergeBestsellers
              ? this.mergeWithBestsellers(validViewed)
              : validViewed;

            this.renderProducts(products);
            if (!this.track.querySelector('.recently-viewed-products__item')) return;

            this.section.setAttribute('data-has-products', 'true');

            if (this.compact) {
              this.section.hidden = true;
            } else {
              this.section.hidden = false;
            }

            this.bindControls();
            requestAnimationFrame(() => this.updateControls());
            this.dispatchEvent(new CustomEvent('recently-viewed:ready', { bubbles: true }));
          })
          .catch(() => {});
      }

      renderProducts(products) {
        this.track.replaceChildren();

        if (!this.isGrouped) {
          products.forEach((product) => this.track.appendChild(this.createCard(product)));
          return;
        }

        const viewed = products.filter((product) => !product.is_bestseller);
        const bestsellers = products.filter((product) => product.is_bestseller);
        // Hide "Recently viewed" title when bestsellers are absent (e.g. 5+ viewed).
        const showViewedTitle = Boolean(viewed.length && bestsellers.length);

        if (viewed.length) {
          this.track.appendChild(
            this.createGroup('viewed', showViewedTitle ? this.viewedHeading : '', viewed)
          );
        }

        if (bestsellers.length) {
          this.track.appendChild(this.createGroup('bestsellers', this.bestsellersHeading, bestsellers));
        }
      }

      createGroup(key, title, products) {
        const group = document.createElement('div');
        group.className = 'recently-viewed-products__group';
        group.dataset.group = key;

        if (title) {
          const heading = document.createElement('h3');
          heading.className = 'recently-viewed-products__group-title';
          heading.textContent = title;
          group.appendChild(heading);
        }

        const list = document.createElement('ul');
        list.className = 'recently-viewed-products__group-list';
        list.setAttribute('role', 'list');

        products.forEach((product) => {
          // Same products as carousel; titles replace card badges in grouped layout.
          list.appendChild(this.createCard({ ...product, badge: '' }));
        });

        group.appendChild(list);
        return group;
      }

      getStoredEntries() {
        return readRecentlyViewedStore(this.dataset.storageKey);
      }

      getBestsellers() {
        const node = this.section.querySelector('[data-recently-viewed-bestsellers]');
        if (!node) return [];

        try {
          const value = JSON.parse(node.textContent || '[]');
          return Array.isArray(value)
            ? value.filter((item) => item && item.handle).slice(0, 4)
            : [];
        } catch (error) {
          return [];
        }
      }

      productKey(product) {
        if (product?.id != null && product.id !== '') return `id:${product.id}`;
        if (product?.handle) return `handle:${product.handle}`;
        return '';
      }

      mergeWithBestsellers(viewedProducts) {
        const merged = [];
        const seen = new Set();

        viewedProducts.forEach((product) => {
          const key = this.productKey(product);
          if (!key || seen.has(key) || merged.length >= this.limit) return;
          seen.add(key);
          merged.push({
            ...product,
            badge: '',
            is_bestseller: false,
          });
        });

        const viewedCount = merged.length;
        if (!viewedCount) return [];

        // 1 viewed -> 4 bestsellers, 2 -> 3, 3 -> 2, 4 -> 1, 5+ -> 0
        const bestsellerSlots = Math.max(0, 5 - viewedCount);
        if (!bestsellerSlots) return merged;

        let bestsellersAdded = 0;
        this.getBestsellers().forEach((product) => {
          const key = this.productKey(product);
          if (!key || seen.has(key) || bestsellersAdded >= bestsellerSlots || merged.length >= this.limit) {
            return;
          }
          seen.add(key);
          bestsellersAdded += 1;
          merged.push({
            ...product,
            badge: product.badge || '',
            is_bestseller: true,
          });
        });

        return merged;
      }

      resolveProduct(entry) {
        return this.fetchProduct(entry.handle).then((remote) => {
          if (!remote && !entry.title) return null;

          return {
            id: entry.id || remote?.id || null,
            handle: entry.handle,
            title: entry.title || remote?.title || '',
            url: entry.url || remote?.url || `/products/${entry.handle}`,
            featured_image: entry.featured_image || remote?.featured_image || '',
            price: Number(remote?.price) || 0,
            compare_at_price:
              remote?.compare_at_price == null || remote?.compare_at_price === ''
                ? null
                : Number(remote.compare_at_price) || 0,
            badge: entry.badge || '',
            is_bestseller: Boolean(entry.is_bestseller),
          };
        });
      }

      fetchProduct(handle) {
        if (!handle) return Promise.resolve(null);
        if (!this.constructor.productCache) this.constructor.productCache = new Map();
        if (this.constructor.productCache.has(handle)) {
          return this.constructor.productCache.get(handle);
        }

        const root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
        // Do not send Accept: application/json — Shopify then ignores ?view= and returns
        // the native product JSON API (no feed_price). Request the Liquid alternate view.
        const request = fetch(`${root}products/${encodeURIComponent(handle)}?view=recently-viewed-price`)
          .then((response) => (response.ok ? response.text() : null))
          .then((text) => {
            if (!text) return null;
            try {
              const data = JSON.parse(text.trim());
              // Guard against native product JSON API responses.
              if (!data || typeof data !== 'object' || data.product) return null;
              return data;
            } catch (error) {
              return null;
            }
          })
          .catch(() => null);

        this.constructor.productCache.set(handle, request);
        return request;
      }

      createCard(product) {
        const item = document.createElement('li');
        item.className = 'recently-viewed-products__item fct__grid-item';

        const wrapper = document.createElement('article');
        wrapper.className = 'recently-viewed-products__card fct-card';

        if (product.badge && !this.isGrouped) {
          const badges = document.createElement('div');
          badges.className = 'recently-viewed-products__badges';

          const badge = document.createElement('span');
          badge.className = 'recently-viewed-products__badge';
          badge.textContent = product.badge;
          badges.appendChild(badge);
          wrapper.appendChild(badges);
        }

        const mediaWrap = document.createElement('div');
        mediaWrap.className = 'recently-viewed-products__media-wrap fct-card__media-wrap';

        const mediaLink = document.createElement('a');
        mediaLink.className = 'recently-viewed-products__media fct-card__media';
        mediaLink.href = product.url;
        mediaLink.setAttribute('aria-label', product.title);

        if (product.featured_image) {
          const image = document.createElement('img');
          image.className = 'recently-viewed-products__image fct-card__img motion-reduce';
          image.src = product.featured_image;
          image.alt = product.title;
          image.loading = 'lazy';
          image.width = 533;
          image.height = 533;
          mediaLink.appendChild(image);
        }
        mediaWrap.appendChild(mediaLink);

        const information = document.createElement('div');
        information.className = 'recently-viewed-products__information fct-card__body';

        const content = document.createElement('div');
        content.className = 'recently-viewed-products__content fct-card__content';

        const heading = document.createElement('h3');
        heading.className = 'recently-viewed-products__name fct-card__title';

        const titleLink = document.createElement('a');
        titleLink.href = product.url;
        titleLink.textContent = product.title;
        heading.appendChild(titleLink);

        const price = document.createElement('div');
        price.className = 'recently-viewed-products__price fct-card__price';
        price.appendChild(this.createPriceMarkup(product));

        content.appendChild(heading);
        information.append(content, price);
        wrapper.append(mediaWrap, information);
        item.appendChild(wrapper);
        return item;
      }

      createPriceMarkup(product) {
        const row = document.createElement('div');
        row.className = 'recently-viewed-products__price-row price';

        const price = Number(product.price) || 0;
        const compareAtPrice = Number(product.compare_at_price) || 0;
        const onSale = compareAtPrice > price;

        if (onSale) {
          const sale = document.createElement('span');
          sale.className = 'recently-viewed-products__price-sale price-item price-item--sale';
          sale.textContent = this.formatMoney(price);

          const compare = document.createElement('s');
          compare.className = 'recently-viewed-products__price-compare price-item price-item--regular';
          compare.textContent = this.formatMoney(compareAtPrice);

          row.append(compare, sale);
          return row;
        }

        const regular = document.createElement('span');
        regular.className = 'recently-viewed-products__price-regular price-item price-item--regular';
        regular.textContent = this.formatMoney(price);
        row.appendChild(regular);
        return row;
      }

      formatMoney(cents) {
        const currency = window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency,
          }).format(Number(cents) / 100);
        } catch (error) {
          return `$${(Number(cents) / 100).toFixed(2)}`;
        }
      }

      bindControls() {
        if (!this.previousButton || !this.nextButton) return;

        [this.previousButton, this.nextButton].forEach((button) => {
          button.addEventListener('pointerdown', (event) => event.preventDefault());
        });
        this.previousButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.scroll(-1);
        });
        this.nextButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.scroll(1);
        });
        this.track.addEventListener('scroll', () => this.updateControls(), { passive: true });
        window.addEventListener('resize', () => this.updateControls());
      }

      scroll(direction) {
        const item = this.track.querySelector('.recently-viewed-products__item');
        if (!item) return;
        const styles = getComputedStyle(this.track);
        const gap =
          parseFloat(styles.columnGap || styles.gap) ||
          parseFloat(getComputedStyle(item.parentElement).columnGap || getComputedStyle(item.parentElement).gap) ||
          0;
        this.track.scrollBy({ left: direction * (item.getBoundingClientRect().width + gap), behavior: 'smooth' });
      }

      updateControls() {
        if (!this.previousButton || !this.nextButton) return;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        this.previousButton.disabled = this.track.scrollLeft <= 2;
        this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      }
    }
  );
}
